import { IProduct } from "src/core/products/api/products.api";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import { RoleType } from "src/core/shared/api/roles.api";
import useAxios from "src/core/shared/hooks/useAxios";
import { IUser } from "src/core/users/api/users.api";
import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/library/utils";

export interface IWaste {
  id: number;
  branchId: number;
  wasteDate: Date;
  productId: number;
  quantity: number;
  reasonCode: WasteReasonCode;
  reportedByUserId?: number;
  reportedByType?: WasteReportedByType;
  createdAt: Date;
  updatedAt: Date;
  reportedByUser?: IUser;
  product: IProduct;
}

export type IWasteCreate = Omit<IWaste, "id" | "createdAt" | "updatedAt">;

export enum WasteReportedByType {
  Admin = "admin",
  Customer = "customer",
  EPOD = "epod"
}

export enum WasteReasonCode {
  OutOfDate = "out-of-date",
  Damaged = "damaged"
}

export type Wastage = IWaste[];
export type CreateWastage = IWasteCreate[];

export interface GetBranchWastageGroupedParams {
  branchId: number;
  pagination: BranchWastageGroupedPagination
  filters: FilterParams<Pick<BranchWastageGrouped, "wasteDate">>;
}

export interface GetBranchWastageParams {
  branchId: number;
  filters: FilterParams<Pick<BranchWastageGrouped, "wasteDate">>;
}

export type BranchWastageGroupedPagination = PaginatedParams<BranchWastageGrouped>;

export interface BranchWastageGrouped {
  wasteDate: Date;
  reportedByUserId: number;
  reportedBy: string;
  reportedByRole: RoleType;
  totalProducts: number;
}

export interface SetBranchWastageParams {
  branchId: number;
  wasteDate: Date;
  wastage: IWasteCreate[];
}

type WasteEndpointConfig = {
  GET_BRANCH_WASTAGE_GROUPED: EndpointConfig<
    GetBranchWastageGroupedParams,
    PaginatedData<BranchWastageGrouped>
  >;
  GET_BRANCH_WASTAGE: EndpointConfig<
    GetBranchWastageParams,
    Wastage
  >;
  SET_BRANCH_WASTAGE: EndpointConfig<
    SetBranchWastageParams,
    Wastage
  >;
};

const BASE_URL = `${process.env.REACT_APP_API_URL}/waste`;

const requests: RequestConfig<WasteEndpointConfig> = {
  GET_BRANCH_WASTAGE_GROUPED: ({ branchId, ...params }) => ({
    url: `${BASE_URL}/branch/${branchId}/grouped`,
    method: "GET",
    params
  }),
  GET_BRANCH_WASTAGE: ({ branchId, ...params }) => ({
    url: `${BASE_URL}/branch/${branchId}`,
    method: "GET",
    params
  }),
  SET_BRANCH_WASTAGE: ({ branchId, ...data }) => ({
    url: `${BASE_URL}/branch/${branchId}/set-wastage`,
    method: "POST",
    data
  })
};

export const useWasteApi: UseApiRequest<WasteEndpointConfig> = name => useAxios(requests[ name ], name);
